/**
 * attendanceDemoService.js
 * Menaxhon attendance në localStorage (demo mode — pa backend)
 * Përdoret kur REACT_APP_API_URL nuk është vendosur.
 */

const PREFIX = 'hr_';
const getStore = (key) => { try { return JSON.parse(localStorage.getItem(PREFIX + key) || '[]'); } catch { return []; } };
const setStore = (key, data) => { try { localStorage.setItem(PREFIX + key, JSON.stringify(data)); } catch {} };

/**
 * Llogarit attendance summary nga localStorage data.
 * E njëjta logjik si SQL query-ja /api/reports/attendance-summary.
 */
export function computeAttendanceSummary({ from, to, worker_id, department_id } = {}) {
  const sessions    = getStore('attendanceSessions');
  const workers     = getStore('workers');
  const depts       = getStore('departments');
  const schedules   = getStore('workSchedules');
  const empSchedules = getStore('employeeSchedules');

  return sessions
    .filter(s => s.date >= (from || '2000-01-01') && s.date <= (to || '9999-12-31'))
    .filter(s => !worker_id || String(s.worker_id) === String(worker_id))
    .filter(s => {
      if (!department_id) return true;
      const w = workers.find(w => Number(w.Id || w.id) === Number(s.worker_id));
      return w && String(w.Department || w.department_id || '') === String(department_id);
    })
    .map(s => {
      const w  = workers.find(w => Number(w.Id || w.id) === Number(s.worker_id)) || {};
      const d  = depts.find(d => Number(d.Id || d.id) === Number(w.Department || w.department_id)) || {};
      const es = empSchedules.find(e => Number(e.worker_id) === Number(s.worker_id) && e.is_active);
      const ws = es ? schedules.find(sc => Number(sc.id) === Number(es.schedule_id)) : null;

      let lateMin = 0, earlyMin = 0, overtMin = 0;
      if (ws && s.check_in) {
        const ci       = new Date(s.check_in);
        const schedSt  = new Date(`${s.date}T${ws.start_time}:00`);
        const diff     = Math.round((ci - schedSt) / 60000);
        lateMin = Math.max(0, diff - (ws.late_tolerance_minutes || 0));
      }
      if (ws && s.check_out) {
        const co     = new Date(s.check_out);
        const schedEn = new Date(`${s.date}T${ws.end_time}:00`);
        earlyMin = Math.max(0, Math.round((schedEn - co) / 60000));
        overtMin = Math.max(0, Math.round((co - schedEn) / 60000));
      }

      const workedMin = s.work_minutes || 0;
      const breakMin  = s.break_minutes || 0;
      const netMin    = Math.max(0, workedMin - breakMin);

      let complianceStatus;
      if (!s.check_in)  complianceStatus = 'absent';
      else if (!s.check_out) complianceStatus = 'in_progress';
      else if (lateMin > 0)  complianceStatus = 'late';
      else if (earlyMin > 0) complianceStatus = 'early_checkout';
      else                   complianceStatus = 'on_time';

      return {
        id:                    s.id,
        date:                  s.date,
        worker_id:             s.worker_id,
        worker_name:           `${w.FirstName||w.first_name||''} ${w.LastName||w.last_name||''}`.trim() || `Worker ${s.worker_id}`,
        department_id:         w.Department || w.department_id || null,
        department_name:       d.Name || d.name || '',
        check_in:              s.check_in,
        check_out:             s.check_out,
        breaks:                s.breaks || [],
        work_minutes:          workedMin,
        break_minutes:         breakMin,
        net_worked_minutes:    netMin,
        late_minutes:          lateMin,
        early_checkout_minutes:earlyMin,
        overtime_minutes:      overtMin,
        status:                s.status,
        location:              s.location || 'office',
        schedule_id:           ws?.id || null,
        schedule_name:         ws?.name || null,
        scheduled_start:       ws?.start_time || null,
        scheduled_end:         ws?.end_time || null,
        scheduled_break_minutes: ws?.break_minutes || null,
        late_tolerance_minutes:  ws?.late_tolerance_minutes || null,
        compliance_status:     complianceStatus,
      };
    });
}

/** Kthen sesionin aktiv (sot) të punëtorit nga localStorage. */
export function getDemoActiveSession(workerId) {
  const today = new Date().toISOString().slice(0, 10);
  return getStore('attendanceSessions').find(
    s => String(s.worker_id) === String(workerId) && s.date === today
  ) || null;
}

/** Kthen orarin aktiv të punëtorit nga localStorage. */
export function getDemoWorkerSchedule(workerId) {
  const schedules    = getStore('workSchedules');
  const empSchedules = getStore('employeeSchedules');
  const es = empSchedules.find(e => Number(e.worker_id) === Number(workerId) && e.is_active);
  if (!es) return null;
  return schedules.find(sc => Number(sc.id) === Number(es.schedule_id)) || null;
}

/**
 * Kryen një veprim attendance (checkin/checkout/break_start/break_end) në localStorage.
 * Kthen { success, data } si backend-i.
 */
export function postDemoAttendanceEvent(workerId, type) {
  const today    = new Date().toISOString().slice(0, 10);
  const sessions = getStore('attendanceSessions');
  const nowStr   = new Date().toISOString().slice(0, 19); // "2026-03-16T08:05:00"
  const action   = type.toLowerCase().replace(/-/g, '_');

  let idx = sessions.findIndex(
    s => String(s.worker_id) === String(workerId) && s.date === today
  );

  if (idx === -1) {
    if (action !== 'checkin') return { success: false, error: 'Nuk ka check-in aktiv sot' };
    const newSess = {
      id:           Math.max(0, ...sessions.map(s => s.id || 0)) + 1,
      worker_id:    Number(workerId),
      date:         today,
      check_in:     nowStr,
      check_out:    null,
      breaks:       [],
      work_minutes: null,
      break_minutes:0,
      status:       'open',
      location:     'office',
    };
    sessions.push(newSess);
    setStore('attendanceSessions', sessions);
    return { success: true, data: newSess };
  }

  const s = { ...sessions[idx] };

  if (action === 'checkin') {
    s.check_in = nowStr;
    s.status   = 'open';
  } else if (action === 'checkout') {
    const ci = new Date(s.check_in);
    const co = new Date();
    s.check_out    = nowStr;
    s.work_minutes = Math.round((co - ci) / 60000);
    s.status       = 'closed';
  } else if (action === 'break_start') {
    const brks = [...(s.breaks || []), { start: nowStr, end: null }];
    s.breaks = brks;
    s.status = 'on_break';
  } else if (action === 'break_end') {
    const brks = [...(s.breaks || [])];
    for (let i = brks.length - 1; i >= 0; i--) {
      if (!brks[i].end) { brks[i] = { ...brks[i], end: nowStr }; break; }
    }
    s.breaks       = brks;
    s.break_minutes = brks.reduce((sum, b) => sum + (b.end ? Math.round((new Date(b.end) - new Date(b.start)) / 60000) : 0), 0);
    s.status       = 'open';
  } else {
    return { success: false, error: `Veprim i panjohur: ${type}` };
  }

  sessions[idx] = s;
  setStore('attendanceSessions', sessions);
  return { success: true, data: s };
}

// ─────────────────────────────────────────────────────────────────────────────
// DAILY DETAIL — pamja mujore ditore per un punëtor
// Ekuivalent me GET /api/reports/attendance-daily?worker_id=&year=&month=
// ─────────────────────────────────────────────────────────────────────────────
const WEEKDAYS_EN = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const WEEKDAYS_SQ = ['E Diel','E Hënë','E Martë','E Mërkurë','E Enjte','E Premte','E Shtunë'];

export function getDailyDetail(workerId, year, month) {
  const y = parseInt(year  || new Date().getFullYear(), 10);
  const m = parseInt(month || new Date().getMonth() + 1, 10);

  const sessions     = getStore('attendanceSessions');
  const workers      = getStore('workers');
  const schedules    = getStore('workSchedules');
  const empSchedules = getStore('employeeSchedules');
  const adjustments  = getStore('attendanceAdjustments');

  const wid  = Number(workerId);
  const w    = workers.find(x => Number(x.Id || x.id) === wid) || {};
  const es   = empSchedules.find(e => Number(e.worker_id) === wid && e.is_active);
  const ws   = es ? schedules.find(sc => Number(sc.id) === Number(es.schedule_id)) : null;

  const workDays = ws?.work_days || ['Monday','Tuesday','Wednesday','Thursday','Friday'];
  const daysInMonth = new Date(y, m, 0).getDate();

  // Planned minutes per day
  let plannedMin = null;
  if (ws?.start_time && ws?.end_time) {
    const [sh, sm2] = ws.start_time.split(':').map(Number);
    const [eh, em2] = ws.end_time.split(':').map(Number);
    plannedMin = (eh * 60 + em2) - (sh * 60 + sm2) - (Number(ws.break_minutes) || 0);
  }

  const rows = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr   = `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    const dayOfWeek = new Date(dateStr).getDay();
    const dayNameEn = WEEKDAYS_EN[dayOfWeek];
    const isWorkDay = workDays.includes(dayNameEn);

    const sess = sessions.find(s => Number(s.worker_id) === wid && s.date === dateStr) || null;

    // Latest adjustment for this day
    const allAdj = (adjustments || [])
      .filter(a => Number(a.worker_id) === wid && a.adj_date === dateStr)
      .sort((a, b) => (b.adjusted_at || '').localeCompare(a.adjusted_at || ''));
    const adj = allAdj[0] || null;

    const ciRaw = sess?.check_in    || null;
    const coRaw = sess?.check_out   || null;
    const brRaw = sess?.break_minutes != null ? Number(sess.break_minutes) : null;
    const wmRaw = sess?.work_minutes  != null ? Number(sess.work_minutes)  : null;

    const ciEff  = adj?.adj_check_in      || ciRaw;
    const coEff  = adj?.adj_check_out     || coRaw;
    const brEff  = adj?.adj_break_minutes != null ? Number(adj.adj_break_minutes) : brRaw;
    const wmEff  = adj?.adj_work_minutes  != null ? Number(adj.adj_work_minutes)  :
                   (ciEff && coEff ? Math.round((new Date(coEff) - new Date(ciEff)) / 60000) : wmRaw);

    const netWorked = wmEff != null && brEff != null ? Math.max(0, wmEff - brEff) :
                      wmEff != null ? wmEff : 0;
    const diffMin   = isWorkDay && plannedMin != null ? netWorked - plannedMin : null;

    let lateMin = 0, earlyMin = 0, overtMin = 0;
    if (ws?.start_time && ciEff) {
      const schedSt = new Date(`${dateStr}T${ws.start_time}:00`);
      const tol = Number(ws.late_tolerance_minutes) || 0;
      lateMin = Math.max(0, Math.round((new Date(ciEff) - schedSt) / 60000) - tol);
    }
    if (ws?.end_time && coEff) {
      const schedEn = new Date(`${dateStr}T${ws.end_time}:00`);
      earlyMin = Math.max(0, Math.round((schedEn - new Date(coEff)) / 60000));
      overtMin = Math.max(0, Math.round((new Date(coEff) - schedEn)  / 60000));
    }

    let complianceStatus;
    if (!isWorkDay)        complianceStatus = 'day_off';
    else if (!ciEff)       complianceStatus = 'absent';
    else if (!coEff)       complianceStatus = 'in_progress';
    else if (lateMin > 0)  complianceStatus = 'late';
    else if (earlyMin > 0) complianceStatus = 'early_checkout';
    else                   complianceStatus = 'on_time';

    rows.push({
      date:                   dateStr,
      day_name:               WEEKDAYS_SQ[dayOfWeek],
      is_work_day:            isWorkDay,
      session_id:             sess?.id || null,
      check_in_raw:           ciRaw,
      check_out_raw:          coRaw,
      break_minutes_raw:      brRaw,
      work_minutes_raw:       wmRaw,
      check_in:               ciEff,
      check_out:              coEff,
      break_minutes:          brEff,
      work_minutes:           wmEff,
      net_worked_minutes:     netWorked,
      planned_minutes:        isWorkDay ? plannedMin : null,
      diff_minutes:           diffMin,
      late_minutes:           lateMin,
      early_checkout_minutes: earlyMin,
      overtime_minutes:       overtMin,
      compliance_status:      complianceStatus,
      is_adjusted:            !!adj,
      adj_id:                 adj?.id || null,
      adj_reason:             adj?.reason || null,
      adj_by_name:            adj?.adjusted_by_name || null,
      adj_at:                 adj?.adjusted_at || null,
    });
  }

  const workerName = `${w.FirstName||w.first_name||''} ${w.LastName||w.last_name||''}`.trim();
  return {
    data: rows,
    worker: {
      id:              wid,
      first_name:      w.FirstName || w.first_name || '',
      last_name:       w.LastName  || w.last_name  || '',
      worker_name:     workerName,
      department_name: '',
      schedule_name:   ws?.name || '',
      start_time:      ws?.start_time || '',
      end_time:        ws?.end_time   || '',
      break_minutes:   ws?.break_minutes || 0,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ADJUSTMENT HISTORY — historiku per nje sesion/ditë
// ─────────────────────────────────────────────────────────────────────────────
export function getAdjustmentHistory(workerId, date) {
  const adjustments = getStore('attendanceAdjustments');
  return (adjustments || [])
    .filter(a => Number(a.worker_id) === Number(workerId) && a.adj_date === date)
    .sort((a, b) => (b.adjusted_at || '').localeCompare(a.adjusted_at || ''));
}

// ─────────────────────────────────────────────────────────────────────────────
// SAVE ADJUSTMENT — ruaj korrigjim manual
// ─────────────────────────────────────────────────────────────────────────────
export function saveAdjustment({
  session_id, worker_id, adj_date,
  adj_check_in, adj_check_out, adj_break_minutes, adj_work_minutes,
  reason,
}) {
  if (!worker_id || !adj_date || !reason?.trim()) {
    return { success: false, error: 'worker_id, adj_date dhe reason janë të detyrueshme' };
  }
  const adjustments = getStore('attendanceAdjustments') || [];
  const sessions    = getStore('attendanceSessions') || [];

  // Find original values for snapshot
  const sess = sessions.find(s => (session_id && s.id === session_id) ||
    (Number(s.worker_id) === Number(worker_id) && s.date === adj_date));

  try {
    const user = JSON.parse(localStorage.getItem('logedInUser') || '{}');
    const uData = user?.data || {};
    const byName = `${uData.FirstName||uData.first_name||''} ${uData.LastName||uData.last_name||''}`.trim() || 'Admin';

    const newAdj = {
      id:                  Math.max(0, ...adjustments.map(a => a.id || 0)) + 1,
      session_id:          sess?.id || session_id || null,
      worker_id:           Number(worker_id),
      adj_date,
      adj_check_in:        adj_check_in  || null,
      adj_check_out:       adj_check_out || null,
      adj_break_minutes:   adj_break_minutes != null ? Number(adj_break_minutes) : null,
      adj_work_minutes:    adj_work_minutes  != null ? Number(adj_work_minutes)  : null,
      reason:              reason.trim(),
      adjusted_by_name:    byName,
      adjusted_at:         new Date().toISOString(),
      orig_check_in:       sess?.check_in  || null,
      orig_check_out:      sess?.check_out || null,
      orig_break_minutes:  sess?.break_minutes != null ? Number(sess.break_minutes) : null,
      orig_work_minutes:   sess?.work_minutes  != null ? Number(sess.work_minutes)  : null,
    };
    adjustments.push(newAdj);
    setStore('attendanceAdjustments', adjustments);
    return { success: true, data: newAdj };
  } catch (e) {
    return { success: false, error: e.message };
  }
}
